import React from 'react';
import type { MultiPortfolioParseResult, Portfolio } from 'venn-api';
import { Icon, ItemIcon, ItemType } from 'venn-ui-kit';
import { isHistoricalPortfolio } from 'venn-utils';
import { isNil } from 'lodash';
import type { MultiPortfolioUploadConfirmationData } from '../types';
import { UniversalUploaderFooter } from '../components/page-parts/UniversalUploaderFooter';
import { ScrollableSectionWithStickyHeader } from './review/ScrollableSectionWithStickyHeader';
import { useOwnershipAssignment } from './hooks/useOwnershipAssignment';
import { cn } from '@/lib/utils';
import { MultiPortfolioReviewContext } from './components/common/MultiPortfolioReviewContext';
import type { PortfolioParseResultWithReviewMetadata } from './components/common/MultiPortfolioReviewContext';
import { OwnershipContextSelector } from '../../components/OwnershipContextSelector';

type AssignOwnershipStepProps = Readonly<{
  parsedData: MultiPortfolioParseResult;
  goBackToReviewStep: () => void;
  goToUploadConfirmation: (data: MultiPortfolioUploadConfirmationData) => void;
}>;

type PortfolioRowProps = Readonly<{
  portfolio: Portfolio;
  onOwnershipChange: ((portfolio: Portfolio, ownerContextId: string) => void) | undefined;
  availableContexts: { value: string; label: string }[] | undefined;
}>;

const PortfolioRow = ({ portfolio, onOwnershipChange, availableContexts }: PortfolioRowProps) => {
  const isNew = isNil(portfolio.id);
  const isHistorical = isHistoricalPortfolio(portfolio);

  return (
    <div className="flex flex-col justify-center py-[8px] px-[24px] min-h-[50px] border-b border-gray-200">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row items-center gap-[4px]">
          <ItemIcon item={isHistorical ? ItemType.HistoricalPortfolio : ItemType.Portfolio} />
          <span className="font-medium text-[14px]">{portfolio.name}</span>
        </div>

        {!isNil(onOwnershipChange) && !isNil(availableContexts) && (
          <div className="flex flex-row items-center gap-[8px]">
            {isNew && !portfolio.ownerContextId && (
              <Icon type="diamond-exclamation" className="text-venn-orange text-[14px]" />
            )}

            <div className="min-w-[200px]">
              <OwnershipContextSelector
                ownerContextId={portfolio.ownerContextId}
                setOwnerContextId={(newOwnerContextId) => onOwnershipChange(portfolio, newOwnerContextId)}
                requiredAction={isNew ? 'CREATE_PORTFOLIO' : 'EDIT_PORTFOLIO'}
                disabled={undefined}
                className="mt-0"
                refreshedStyling
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AssignOwnershipStep = ({ goBackToReviewStep }: Omit<AssignOwnershipStepProps, 'parsedData'>) => {
  const { availableContexts, allAssigned, existingPortfolios, newPortfolios, handleOwnershipChange } =
    useOwnershipAssignment();
  const { excludedParsedData } = React.useContext(MultiPortfolioReviewContext).data;
  const { savePortfolios } = React.useContext(MultiPortfolioReviewContext).actions;

  return (
    <div className="flex flex-col items-start flex-1 overflow-hidden w-full">
      <div
        className={cn(
          'px-[24px] py-[8px] w-full',
          allAssigned ? 'bg-venn-green text-venn-white' : 'bg-venn-warning text-venn-black',
        )}
      >
        <div className="flex items-center gap-[8px]">
          {allAssigned ? (
            <>
              <Icon type="check" />
              <span>All portfolios assigned ownership.</span>
            </>
          ) : (
            <>
              <Icon type="diamond-exclamation" className="text-orange-500" />
              <span>Please ensure all portfolios have ownership assigned before continuing.</span>
            </>
          )}
        </div>
      </div>
      <div className="flex-1 w-full overflow-auto">
        {existingPortfolios.length > 0 && (
          <ScrollableSectionWithStickyHeader
            key="existing"
            title="Existing Portfolios Updated"
            count={existingPortfolios.length}
            groupIndex={0}
          >
            {existingPortfolios.map((portfolio) => (
              <PortfolioRow
                key={portfolio.id ?? portfolio.name}
                portfolio={portfolio}
                onOwnershipChange={handleOwnershipChange}
                availableContexts={availableContexts}
              />
            ))}
          </ScrollableSectionWithStickyHeader>
        )}

        {newPortfolios.length > 0 && (
          <ScrollableSectionWithStickyHeader
            key="new"
            title="New Portfolios Created"
            count={newPortfolios.length}
            groupIndex={existingPortfolios.length > 0 ? 1 : 0}
          >
            {newPortfolios.map((portfolio) => (
              <PortfolioRow
                key={portfolio.id ?? portfolio.name}
                portfolio={portfolio}
                onOwnershipChange={handleOwnershipChange}
                availableContexts={availableContexts}
              />
            ))}
          </ScrollableSectionWithStickyHeader>
        )}
        {excludedParsedData.length > 0 && (
          <ScrollableSectionWithStickyHeader
            key="excluded"
            title="Excluded From Upload"
            count={excludedParsedData.length}
            groupIndex={
              existingPortfolios.length > 0 && newPortfolios.length > 0
                ? 2
                : existingPortfolios.length > 0 || newPortfolios.length > 0
                  ? 1
                  : 0
            }
          >
            {excludedParsedData.map(({ parsedPortfolio }: PortfolioParseResultWithReviewMetadata) => (
              <PortfolioRow
                key={parsedPortfolio.id ?? parsedPortfolio.name}
                portfolio={parsedPortfolio}
                onOwnershipChange={undefined}
                availableContexts={undefined}
              />
            ))}
          </ScrollableSectionWithStickyHeader>
        )}
      </div>

      <UniversalUploaderFooter
        noMargin
        onCancel={goBackToReviewStep}
        onContinue={savePortfolios}
        primaryLabel="Complete Upload"
        cancelLabel="< BACK"
        disabled={!allAssigned}
      />
    </div>
  );
};

export default AssignOwnershipStep;
